import { MainLayout, SkipLink } from '@salecycle/ui-component-library';
import { ConnectedRouter } from 'connected-react-router';
import { useAppContext, useFlags, usePermissions } from 'core/app/AppContext';
import { usePreferencesState } from 'core/preferences/usePreferencesContext';
import Notifications from 'core/notifications/Notifications';
import { AuthorisedRoute } from 'core/router/AuthorisedRoute';
import AppLoader from 'core/ui/components/AppLoader';
import ModularNav from 'core/ui/components/ModularNav/ModularNav';
import { DefaultErrorPage, NotFoundPage } from 'core/ui/components/error';
import NotificationBar from 'core/ui/notificationBar';
import SearchOverlay from 'core/ui/SearchOverlay/SearchOverlay';
import React, { FC, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Redirect, Switch } from 'react-router-dom';
import LookerEmbedDashboardPage from 'sections/looker-dashboard/components/LookerEmbedDashboardPage';
import SftpUserManagementPage from 'sections/user-management/components/SftpUserManagementPage';
import { EditUserPage } from 'sections/user-management/sections/edit-user/EditUserPage';
import { EditSftpUserPage } from 'sections/user-management/sections/edit-sftp-user/EditSftpUserPage';
import { RolesBasePage } from 'sections/user-management/sections/roles/RolesBasePage';
import NewUserWizardPage from 'sections/user-management/sections/new-user/components/NewUserWizardPage';
import { Selectors as FeatureFlagSelectors } from 'services/launchDarkly/';
import { State } from 'store';
import routerHistory from 'store/history';
import { HomePage } from './HomePage';
import { ReportableDashboardPage } from 'sections/reportable-data-dashboard/components/ReportableDashboardPage';
import { FileExportPage } from 'sections/file-management/pages/FileExportPage';
import { ConsentAnalysisPage } from 'sections/file-management/pages/ConsentAnalysisPage';
import { FileManagementRoute } from 'sections/file-management/routes';
import { ConsentUploadPage } from 'sections/file-management/pages/ConsentUploadPage';
import NewSftpUserWizardPage from 'sections/user-management/sections/new-sftp-user/components/NewSftpUserWizardPage';
import { ConsentUploadSuccessPage } from 'sections/file-management/pages/ConsentUploadSuccessPage';
import { TemplateManagementRoute } from 'sections/design/template-management/routes';
import TemplateListPage from 'sections/design/template-management/sections/template-list/TemplatesListPage';
import TemplateCreatePage from 'sections/design/template-management/sections/template-create/TemplateCreatePage';
import TemplateEditorPage from 'sections/design/template-management/sections/template-editor/TemplateEditorPage';
import { ClientRoute } from 'sections/client-management/routes/ClientRoute';
import { FrequencyCapsListPage } from 'sections/client-management/ui/pages/client-settings/frequency-caps/pages/FrequencyCapsListPage';
import { BlocklistAllowlistPage } from 'sections/client-management/ui/pages/client-settings/blocklist-allowlist/pages/BlocklistAllowlistPage';
import { MarketingPermissionsRoute } from '../../sections/client-management/ui/pages/client-settings/marketing-permissions/routes';
import { CreateFileExportConfigPage } from 'sections/file-management/pages/CreateFileExportConfigPage';
import { EditFileExportConfigPage } from 'sections/file-management/pages/EditFileExportConfigPage';
import TemplatePreviewPage from 'sections/design/template-management/sections/template-preview/TemplatePreviewPage';
import { SendRulesPage } from 'sections/compliance/pages/SendRulesPage';
import { TestSendRulesPage } from 'sections/compliance/pages/TestSendRulesPage';
import { ConsentHistoryPage } from 'sections/compliance/pages/ConsentHistoryPage';
import { SelectClientModal } from './components/SelectClientModal/SelectClientModal';
import { OnsiteTemplateCreate } from 'sections/design/template-management/sections/template-create/OnsiteTemplateCreate';
import Campaigns from 'sections/campaigns/ui/Campaigns';
import { RcsTemplateEditorPage } from 'sections/design/template-management/sections/rcs-template-editor/RcsTemplateEditorPage';
import TemplateCopyEditorPage from 'sections/design/template-management/sections/template-copy-editor/TemplateCopyEditorPage';
import { useFeatureFlags } from 'core/app/AppContext';

// Lazy-loaded route components (Vite/Rollup code-splits these into separate chunks)
const Profile = lazy(() => import('core/ui/profile/profile'));
const IntegrationsBase = lazy(() =>
  import('sections/client-management/ui/pages/client-settings/integrations/components/IntegrationsBase'),
);
const SettingsOverview = lazy(() => import('sections/client-management/ui/pages/client-settings/SettingsOverview'));
const TrackingLinksPage = lazy(() =>
  import('sections/client-management/ui/pages/client-settings/tracking-links/components/TrackingLinksPage'),
);
const VoucherConfiguration = lazy(() =>
  import('sections/client-management/ui/pages/client-settings/voucher-codes/components/VoucherConfiguration'),
);
const CreationWizard = lazy(() => import('sections/client-management/ui/pages/creation-wizard/CreationWizard'));
const AssetManagementBase = lazy(() => import('sections/design/asset-management/ui/AssetManagementBase'));
const UserManagementPage = lazy(() => import('sections/user-management/components/UserManagementPage'));

// Lazy loaded routes
const TemplateArchivePage = lazy(() =>
  import('sections/design/template-management/sections/template-archive/TemplateArchive'),
);

const withSuspense = <P extends object>(Component: React.LazyExoticComponent<React.ComponentType<P>>) => {
  const Wrapper = (props: P) => (
    <Suspense fallback={<AppLoader />}>
      <Component {...props} />
    </Suspense>
  );
  return Wrapper;
};

interface IProps {
  featureFlagsInitialised: boolean;
  pathname: string;
}

export const Router: FC<IProps> = props => {
  const { t } = useTranslation();

  const { isAppStateLoading } = useAppContext();
  const { hasFlag } = useFlags();
  const { hasPermission } = usePermissions();
  const { gradientBarVariation } = usePreferencesState();
  const { flagEnabled } = useFeatureFlags();

  if (props.featureFlagsInitialised === true && isAppStateLoading === false) {
    return (
      <ConnectedRouter history={routerHistory}>
        <MainLayout displayGradient={true} gradientVariation={gradientBarVariation}>
          <SkipLink text={t('common.skipLink')} anchor="#mainArea" />
          <NotificationBar />
          <Notifications />
          <SearchOverlay />
          <ModularNav />
          <SelectClientModal />
          <Switch>
            <Redirect
              exact={true}
              from="/user-management"
              to={`/user-management${
                hasPermission('ManageUsers') ? '/users' : hasPermission('ManageSftpUsers') ? '/sftp-users' : ''
              }`}
            />
            <Redirect exact={true} from="/sites/:siteId/campaigns" to="/sites/:siteId/campaigns/offsite" />
            <Redirect
              exact={true}
              from="/sites/:siteId/designs"
              to={`/sites/:siteId/designs/${hasPermission('ManageTemplates') ? 'templates' : 'asset-management'}`}
            />
            <Redirect exact={true} from="/designs" to="/designs/asset-management" />
            <Redirect exact={true} from="/sites/:siteId/settings" to="/sites/:siteId/settings/settings" />
            <Redirect exact={true} from="/brands/:brandId/settings" to="/brands/:brandId/settings/settings" />
            <Redirect exact={true} from="/groups/:groupId/settings" to="/groups/:groupId/settings/settings" />
            <AuthorisedRoute path="/profile" component={withSuspense(Profile)} exact={true} />
            <AuthorisedRoute
              path="/user-management/users/new-user"
              component={NewUserWizardPage}
              requiredAccessLevel="SaleCycle"
              requiredPermissions={['ManageUsers']}
            />
            <AuthorisedRoute
              path="/user-management/users/edit-user/:sub"
              component={EditUserPage}
              requiredAccessLevel="SaleCycle"
              requiredPermissions={['ManageUsers']}
            />
            <AuthorisedRoute
              path="/user-management/users"
              component={withSuspense(UserManagementPage)}
              requiredAccessLevel="SaleCycle"
              requiredPermissions={['ManageUsers']}
              exact={true}
            />
            <AuthorisedRoute
              path="/user-management/sftp-users"
              component={SftpUserManagementPage}
              requiredAccessLevel="SaleCycle"
              requiredPermissions={['ManageSftpUsers']}
              exact={true}
            />
            <AuthorisedRoute
              path="/user-management/sftp-users/new-sftp-user"
              component={NewSftpUserWizardPage}
              requiredAccessLevel="SaleCycle"
              requiredPermissions={['ManageSftpUsers']}
            />
            <AuthorisedRoute
              path="/user-management/sftp-users/edit-sftp-user/:username"
              component={EditSftpUserPage}
              requiredAccessLevel="SaleCycle"
              requiredPermissions={['ManageSftpUsers']}
            />
            <AuthorisedRoute
              path="/sites/:siteId/campaigns"
              component={Campaigns}
              requiredPermissions={['ManageCampaigns']}
            />
            <AuthorisedRoute
              exact={true}
              path={ClientRoute.FREQ_CAPS_LIST}
              component={FrequencyCapsListPage}
              requiredPermissions={['ManageCampaigns']}
            />
            <AuthorisedRoute
              exact={true}
              path={ClientRoute.ALLOW_LIST}
              component={BlocklistAllowlistPage}
              requiredPermissions={['ManageCampaigns']}
            />
            <AuthorisedRoute
              exact={true}
              path={ClientRoute.BLOCK_LIST}
              component={BlocklistAllowlistPage}
              requiredPermissions={['ManageCampaigns']}
            />
            <AuthorisedRoute
              exact={true}
              path="/sites/:siteId/settings/integrations/new-integration"
              component={withSuspense(IntegrationsBase)}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              exact={true}
              path="/sites/:siteId/settings/integrations/:integrationType/:integrationName/:integrationId?"
              component={withSuspense(IntegrationsBase)}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              exact={true}
              path="/sites/:siteId/settings/voucher-codes/:edit"
              component={withSuspense(VoucherConfiguration)}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              exact={true}
              path="/sites/:siteId/settings/integrations"
              component={withSuspense(SettingsOverview)}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              exact={true}
              path="/sites/:siteId/settings/settings"
              component={withSuspense(SettingsOverview)}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              exact={true}
              path="/sites/:siteId/settings/voucher-codes"
              component={withSuspense(SettingsOverview)}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              path="/sites/:siteId/settings/tracking-links"
              component={withSuspense(TrackingLinksPage)}
              requiredPermissions={['ManageClients']}
              requiredFlags={['tracking-links']}
            />
            <AuthorisedRoute
              exact={true}
              path="/brands/:brandId/settings/voucher-codes/:edit"
              component={withSuspense(VoucherConfiguration)}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              exact={true}
              path="/brands/:brandId/settings/settings"
              component={withSuspense(SettingsOverview)}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              exact={true}
              path="/brands/:brandId/settings/voucher-codes"
              component={withSuspense(SettingsOverview)}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              exact={true}
              path="/groups/:groupId/settings/settings"
              component={withSuspense(SettingsOverview)}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              exact={true}
              path={MarketingPermissionsRoute.HOME}
              component={withSuspense(SettingsOverview)}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              exact={true}
              path={ClientRoute.SUPPORTED_LANGUAGES}
              component={withSuspense(SettingsOverview)}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              exact={true}
              path="/brands/:brandId/settings/integrations"
              component={withSuspense(SettingsOverview)}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              exact={true}
              path="/groups/:groupId/settings/integrations"
              component={withSuspense(SettingsOverview)}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              exact={true}
              path="/sites/:siteId/designs/asset-management"
              component={withSuspense(AssetManagementBase)}
              requiredPermissions={['ManageAssets']}
            />
            <AuthorisedRoute
              exact={true}
              path="/designs/asset-management"
              component={withSuspense(AssetManagementBase)}
              requiredPermissions={['ManageAssets']}
            />
            <AuthorisedRoute
              exact={true}
              path={TemplateManagementRoute.ARCHIVE}
              component={withSuspense(TemplateArchivePage)}
              requiredPermissions={['ManageTemplates']}
            />
            <AuthorisedRoute
              exact={true}
              path={TemplateManagementRoute.HOME}
              component={TemplateListPage}
              requiredPermissions={['ManageTemplates']}
            />
            <AuthorisedRoute
              path={TemplateManagementRoute.EDIT_RCS}
              component={RcsTemplateEditorPage}
              requiredPermissions={['ManageTemplates']}
              requiredFlags={['ld-rcs']}
            />
            <AuthorisedRoute
              path={TemplateManagementRoute.EDIT}
              component={TemplateEditorPage}
              requiredPermissions={['ManageTemplates']}
            />
            {flagEnabled('ld-copy-editor') && (
              <AuthorisedRoute
                path={TemplateManagementRoute.EDIT_COPY}
                component={TemplateCopyEditorPage}
                requiredPermissions={['ManageTemplates']}
              />
            )}
            <AuthorisedRoute
              path={TemplateManagementRoute.EDIT_ONSITE}
              component={TemplateEditorPage}
              requiredPermissions={['ManageTemplates']}
            />
            <AuthorisedRoute
              path={TemplateManagementRoute.EDIT_UNSUBSCRIBE}
              component={TemplateEditorPage}
              requiredPermissions={['ManageTemplates']}
            />
            <AuthorisedRoute
              path={TemplateManagementRoute.EDIT_EMAIL_TRACKING_UNSUBSCRIBE}
              component={TemplateEditorPage}
              requiredPermissions={['ManageTemplates']}
            />

            <AuthorisedRoute
              path={TemplateManagementRoute.PREVIEW}
              component={TemplatePreviewPage}
              requiredPermissions={['ManageTemplates']}
            />

            <AuthorisedRoute
              exact={true}
              path={TemplateManagementRoute.CREATE}
              component={props.pathname.includes('mps') ? OnsiteTemplateCreate : TemplateCreatePage}
              requiredPermissions={['ManageTemplates']}
            />
            <AuthorisedRoute
              exact={true}
              path={TemplateManagementRoute.CREATE_ONSITE}
              component={OnsiteTemplateCreate}
              requiredPermissions={['ManageTemplates']}
            />

            <AuthorisedRoute
              path="/create"
              component={withSuspense(CreationWizard)}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              path="/groups/:groupId/create"
              component={withSuspense(CreationWizard)}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              path="/brands/:brandId/create"
              component={withSuspense(CreationWizard)}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              path="/user-management/roles"
              component={RolesBasePage}
              requiredAccessLevel="SaleCycle"
              requiredPermissions={['ManageUsers']}
            />
            <AuthorisedRoute
              exact={true}
              path="/sites/:siteId/dashboard"
              component={LookerEmbedDashboardPage}
              requiredPermissions={['ViewDashboards']}
            />
            <AuthorisedRoute
              exact={true}
              path="/sites/:siteId/dashboard/:dashboardId"
              component={LookerEmbedDashboardPage}
              requiredPermissions={['ViewDashboards']}
            />
            <AuthorisedRoute
              exact={true}
              path="/brands/:brandId/dashboard"
              component={LookerEmbedDashboardPage}
              requiredFlags={['mps-dashboard-brands']}
              requiredPermissions={['ViewDashboards']}
            />
            <AuthorisedRoute
              exact={true}
              path="/brands/:brandId/dashboard/:dashboardId"
              component={LookerEmbedDashboardPage}
              requiredFlags={['mps-dashboard-brands']}
              requiredPermissions={['ViewDashboards']}
            />
            <AuthorisedRoute
              exact={true}
              path="/sites/:siteId/reportable-dashboard"
              component={ReportableDashboardPage}
              requiredPermissions={['ViewDashboards']}
              requiredFlags={['tabbed-dashboard']}
            />
            {/** File-Management routes - START */}
            <Redirect exact={true} from={FileManagementRoute.HOME} to={FileManagementRoute.IMPORT_ANALYSIS} />
            <AuthorisedRoute
              exact={true}
              path={[FileManagementRoute.IMPORT_ANALYSIS, FileManagementRoute.CLIENT_IMPORT_ANALYSIS]}
              component={ConsentAnalysisPage}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              exact={true}
              path={[FileManagementRoute.CLIENT_IMPORT_UPLOAD]}
              component={ConsentUploadPage}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              exact={true}
              path={[FileManagementRoute.CLIENT_IMPORT_UPLOAD_SUCCESS]}
              component={ConsentUploadSuccessPage}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              exact={true}
              path={FileManagementRoute.FILE_EXPORT}
              component={FileExportPage}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              exact={true}
              path={FileManagementRoute.FILE_EXPORT_CONFIG_CREATE}
              component={CreateFileExportConfigPage}
              requiredPermissions={['ManageClients']}
            />
            <AuthorisedRoute
              exact={true}
              path={FileManagementRoute.FILE_EXPORT_CONFIG_EDIT}
              component={EditFileExportConfigPage}
              requiredPermissions={['ManageClients']}
            />

            {/** File-Management routes - END */}
            {/** Compliance routes - START */}
            {hasFlag('compliance-send-rules') && (
              <Redirect exact={true} from="/sites/:sites/compliance" to="/sites/:sites/compliance/send-rules" />
            )}
            {hasFlag('compliance-consent-history') && !hasFlag('compliance-send-rules') && (
              <Redirect exact={true} from="/sites/:sites/compliance" to="/sites/:sites/compliance/consent-history" />
            )}
            <AuthorisedRoute
              path={'/sites/:siteId/compliance/send-rules'}
              component={SendRulesPage}
              requiredAccessLevel="SaleCycle"
              requiredPermissions={['ManageClients']}
              exact={true}
            />
            <AuthorisedRoute
              path={'/sites/:siteId/compliance/test-send-rules'}
              component={TestSendRulesPage}
              requiredAccessLevel="SaleCycle"
              requiredPermissions={['ManageClients']}
              exact={true}
            />
            <AuthorisedRoute
              path={'/sites/:siteId/compliance/consent-history'}
              component={ConsentHistoryPage}
              requiredAccessLevel="SaleCycle"
              requiredPermissions={['ManageClients']}
              exact={true}
            />
            {/** Compliance routes - END */}
            <AuthorisedRoute exact={true} path="/" component={HomePage} requiredPermissions={['ViewDashboards']} />
            <AuthorisedRoute
              exact={true}
              path="/dashboard/:dashboardId"
              component={LookerEmbedDashboardPage}
              requiredPermissions={['ViewDashboards']}
            />
            <AuthorisedRoute exact={true} path="/error" component={DefaultErrorPage} />
            <AuthorisedRoute component={NotFoundPage} />
          </Switch>
        </MainLayout>
      </ConnectedRouter>
    );
  } else {
    return null;
  }
};

const mapState = (state: State): IProps => {
  return {
    featureFlagsInitialised: FeatureFlagSelectors.hasInitialised(state),
    pathname: state.router.location.pathname,
  };
};

export default connect(mapState)(Router);
